<template>
  <div class="registration-from-qr">
    <template v-if="!isRegistered">
      <div class="registration-from-qr__title mb-9">{{ $t('loginForm.createAccount') }}</div>
      <v-form ref="form" v-model="valid" lazy-validation id="authForm">
        <v-container class="px-0 py-0">
          <v-row class="mx-0 my-0 px-0 py-0 justify-space-between container-wrapper" style="column-gap: 10px">
            <v-col class="px-0 py-0" style="max-width: 380px">
              <v-row class="mx-0 my-0">
                <v-col class="px-0 py-0">
                  <div class="label mb-1">{{ $t('loginForm.name') }}</div>
                  <Input ref="firstNameInput"
                         input-class="registration"
                         outlined
                         height="56"
                         radius="16"
                         tabindex="3"
                         background-color="#EDF2F7"
                         :placeholder="String($t('loginForm.yourFirstName'))"
                         :rules="notEmptyRule"
                         @input="form.firstName=$event"
                         @enter="register"
                  />
                </v-col>
              </v-row>
              <v-row class="mx-0 my-0">
                <v-col class="px-0 py-0" style="position: relative">
                  <div class="label mb-1">{{ $t('loginForm.lastName') }}</div>
                  <Input ref="lastNameInput"
                         input-class="registration"
                         outlined
                         height="56"
                         radius="16"
                         tabindex="4"
                         background-color="#EDF2F7"
                         :placeholder="String($t('loginForm.yourLastName'))"
                         :rules="notEmptyRule"
                         @input="form.lastName=$event"
                         @enter="register"
                  />
                </v-col>
              </v-row>
              <v-row class="mx-0 mt-0 mb-7">
                <v-col class="px-0 py-0">
                  <div class="label mb-1">{{ $t('loginForm.gender') }}</div>
                  <div class="gender-switcher d-flex">
                    <Button
                      block
                      dark
                      :class="{ 'active-gender': form.sex === 'Male' }"
                      tabindex="5"
                      height="32"
                      @click.native="updateGender('Male')">
                      {{ $t('loginForm.male') }}
                    </Button>
                    <Button
                      block
                      dark
                      :class="{ 'active-gender': form.sex === 'Female' }"
                      tabindex="6"
                      height="32"
                      @click.native="updateGender('Female')">
                      {{ $t('loginForm.female') }}
                    </Button>
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="px-0 py-0 d-flex flex-column" style="max-width: 380px">
              <div class="mb-11">
                <v-row class="mx-0 my-0">
                  <v-col class="px-0 py-0">
                    <div class="label mb-1">{{ $t('loginForm.email') }}</div>
                    <Input ref="emailInput"
                           input-class="registration"
                           outlined
                           height="56"
                           radius="16"
                           tabindex="7"
                           background-color="#EDF2F7"
                           :placeholder="String($t('loginForm.yourEmail'))"
                           :rules="emailRules"
                           @blur="checkEmail"
                           @input="form.email=$event"
                           @enter="register"
                    />
                  </v-col>
                </v-row>
                <v-row class="mx-0 my-0">
                  <v-col class="px-0 py-0">
                    <div class="label mb-1">{{ $t('loginForm.phone') }}</div>
                    <PhoneInput
                      ref="phoneInput"
                      height="56"
                      radius="16"
                      tabindex="8"
                      :value="form.phone"
                      @input="form.phone=$event"
                      :rules="phoneRules"
                    />
                  </v-col>
                </v-row>
                <v-row class="mx-0 my-0">
                  <v-col class="px-0 py-0">
                    <div class="label mb-1">{{ $t('loginForm.dietary') }}</div>
                    <div class="d-flex registration-from-qr__dietary">
                      <v-checkbox
                        class="pl-0 pr-2 py-3 mt-0"
                        color="#008C89"
                        tabindex="9"
                        v-model="form.vegetarian"
                        :label="$t('loginForm.vegetarian')"/>
                      <v-checkbox
                        color="#008C89"
                        tabindex="10"
                        class="pl-0 pr-2 py-3 mt-0 ml-7"
                        v-model="form.glutenFree"
                        :label="$t('loginForm.glutenFree')"/>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>
          <v-row class="mx-0 my-0 px-0 py-0 justify-space-between container-wrapper" style="column-gap: 10px">
            <v-col class="px-0 py-0" style="max-width: 380px">
              <v-row class="mx-0 mb-4" style="margin-top: 15px">
                <v-col class="px-0 py-0">
                  <div class="registration-from-qr__agree d-flex align-start">
                    <checkbox
                      ref="agreeCheckbox"
                      color="#008C89"
                      tabindex="11"
                      :rules="regAgreeRules"
                      @change="form.isAgreedToTerms=$event"
                    />
                    <span class="mr-2">{{ $t('loginForm.agree') }}</span>
                    <a
                      @click="openTermConditions"
                      style="color: #668BFF; font-weight: 600">{{ $t('loginForm.terms') }}</a>
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="px-0 py-0 d-flex flex-column" style="max-width: 380px">
              <v-row class="my-0 mx-0 mb-7">
                <v-col class="px-0 py-0 mt-auto">
                  <div v-if="commonError && commonError.length" style="line-height: 12px;font-size: 12px;color: #ff5252;margin-bottom: 5px;">
                    <div v-for="(err, idx) in commonError" :key="idx">
                      {{err}}
                    </div>
                  </div>
                  <div>
                    <Button btn-bg="dark-green" tabindex="12" @click.native="register" height="56" block dark>{{ $t('loginForm.create') }}</Button>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <div v-if="loading" class="loading d-flex justify-center px-2 py-2 align-center">
        <v-progress-circular
          indeterminate
          color="#008C89"
        ></v-progress-circular>
      </div>
    </template>
    <template v-else>
      <div class="registered">
        <img src="../../../assets/icons/icon-done-2.svg" :alt="String($t('alt.almost_done'))">
        <h2 class="registered__title mb-5 mt-6">{{ $t('loginForm.niceAlmostDone') }}</h2>
        <div class="registered__info">
          <v-row class="mx-0 mt-0">
            <v-col class="px-0 py-0">
              <p class="registered__info-top-text mb-5">{{ $t('loginForm.checkInbox') }}</p>
              <p class="registered__info-text">
                {{ $t('loginForm.confirmationSent') }}
                <br>
                <span>{{ form.email }}</span>
              </p>
            </v-col>
          </v-row>
        </div>
      </div>
    </template>
    <terms-conditions v-if="dialogTermsConditions.isOpen"/>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import Validation from '@/mixins/ValidationMixin'
import { emailRules, notEmptyRule, phoneRules, regAgreeRules } from '@/utils/validation'
import Input from '@/components/elements/Input'
import PhoneInput from '@/components/elements/phone-input'
import checkbox from '@/components/elements/checkbox'
import Button from '@/components/elements/Button'
import TermsConditions from '@/components/layouts/dialogs/terms-conditions'

export default {
  name: 'registration-from-qr',
  mixins: [Validation],
  props: {
    responseData: Object
  },
  components: { Input, PhoneInput, checkbox, Button, TermsConditions },
  data () {
    return {
      valid: true,
      form: {
        URLKey: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        sex: '',
        vegetarian: false,
        glutenFree: false,
        isAgreedToTerms: false,
        isCaptchaChecked: true
      },
      isRegistered: false,
      commonError: [],
      loading: false,
      emailRules: [],
      notEmptyRule: [],
      phoneRules: [],
      regAgreeRules: [],
      registrationFailedOnce: false
    }
  },
  mounted () {
    this.setLocalizedTitle()

    const t = this.$t.bind(this)
    this.emailRules = emailRules(t)
    this.notEmptyRule = notEmptyRule(t)
    this.phoneRules = phoneRules(t)
    this.regAgreeRules = regAgreeRules()
  },
  methods: {
    ...mapActions(['registrationFromQr', 'vailidateEmail']),
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    setLocalizedTitle () {
      document.title = this.$t('titles.registration')
    },
    updateValidationRule(val) {
      this.regAgreeRules = val
        ? []
        : [v => !!v || this.$t('validation.mustAgree')]
    },
    updateGender (lang) {
      this.form.sex = lang
    },
    async checkEmail () {
      const { email } = this.form
      const params = { email, URLKey: this.$route.params.dynamicUrl }
      const res = await this.vailidateEmail(params)
      this.setDynamicValidationRules({
        emailRules: [
          v => /.+@.+\..+/.test(v) || this.$t('validation.email'),
          res.data.validationResults.isValid || res.data.message
        ]
      })
    },
    async register () {
      this.loading = true
      this.form.URLKey = this.$route.params.dynamicUrl

      if (this.validate()) {
        const result = await this.registrationFromQr(this.form)
        if (result && result?.data && result?.data?.success && result.data.validationResults.isValid) {
          this.isRegistered = true
          this.$emit('showImage')
        }
        if (result?.data?.errors || (result?.data?.validationResults && result.data.validationResults.isValid === false)) {
          this.registrationFailedOnce = true

          const legacyErrors = result.data.errors
            ? Object.keys(result.data.errors)
              .filter(key => key !== '$id')
              .flatMap(key => result.data.errors[key])
            : []

          const validationErrors = result.data.validationResults?.results?.map(e => e.message) || []

          this.commonError = [...new Set([...legacyErrors, ...validationErrors])]
        }
      }
      this.loading = false
    },
    openTermConditions () {
      this.TOGGLE_DIALOG({ name: 'termAndConditions', isOpen: true })
    },
    updateNotEmptyRule(val) {
      this.notEmptyRule = [
        v => !!v || this.$t('validation.required')
      ]
    }
  },
  computed: {
    captchaKey () {
      return process.env.VUE_APP_GOOGLE_API_TOKERN
    },
    ...mapState({
      messages: state => state.messages,
      dialogTermsConditions: state => state.dialogs.termAndConditions
    })
  },
  watch: {
    '$i18n.locale': {
      handler(newLang, oldLang) {
        if (newLang !== oldLang) {
          this.setLocalizedTitle()

          this.$nextTick(() => {
            this.$refs.firstNameInput?.validate?.()
            this.$refs.lastNameInput?.validate?.()
            this.$refs.phoneInput?.validateLang?.()
            this.$refs.agreeCheckbox?.validate?.()
            this.$refs.emailInput?.validate?.()
          })

          if (this.form.email && this.emailRules.some(rule => typeof rule === 'string' || typeof rule === 'function' && !rule(this.form.email))) {
            this.checkEmail()
          }

          if (this.registrationFailedOnce) {
            this.register()
          }
        }
      },
      immediate: false
    },
    'form.firstName' (val) {
      this.updateNotEmptyRule(val)
    },
    'form.lastName' (val) {
      this.updateNotEmptyRule(val)
    },
    'form.phone' (val) {
      this.phoneRules = [
        v => !!v || this.$t('validation.phoneRequired'),
        v => (v && v.replace(/\D/g, '').length >= 10) || this.$t('validation.phoneInvalid')
      ]
    },
    'form.email' (val) {
      if (!val) this.regEmailRules = []
      if (val) {
        this.regEmailRules = [
          v => !!v || this.$t('validation.emailRequired'),
          v => /.+@.+\..+/.test(v) || this.$t('validation.email')
        ]
      }
    },
    'form.isAgreedToTerms' (val) {
      this.updateValidationRule(val)
    },
    messages (val) {
      if (val && val.length > 0) {
        let errMessages = ''
        for (let i = 0; i < val.length; i++) {
          errMessages += `${val[i].msg} `
        }
        this.regEmailRules = [v => errMessages]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.loading {
  min-height: 100px;
  position: absolute;
  height: 95%;
  width: 110%;
  top: 35px;
  left: -20px;
  background: rgba(213, 213, 213, 0.5);
  border-radius: 16px;
}
.label {
  font-size: 14px;
  font-weight: 500;
  color: #425466;
}
.registration-from-qr {
  width: 100%;
  position: relative;
  &__registration-link {
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #72767F;

    ::v-deep .custom-link {
      a {
        color: #668BFF;
      }
    }
  }
  &__agree {
    font-weight: 400;
    font-size: 16px;
    color: #2D3748;
    position: relative;
    ::v-deep .v-input {
      .v-input__slot {
        margin: 0;

        .v-input--selection-controls__input {
          margin-right: 12px;

          .v-icon {
            color: #C9CED6;
          }
        }
      }
    }
    ::v-deep .v-messages.error--text {
      position: absolute;
      top: 23px;
    }
  }
  &__title {
    font-weight: 600;
    font-size: 28px;
    color: #27272E;
    line-height: 38px;
  }
  &__dietary {
    ::v-deep .v-input {
      .v-input__slot {
        margin: 0;
        .v-input--selection-controls__input {
          margin-right: 12px;
          .v-icon {
            color: #C9CED6;
          }
        }
        .v-label {
            color: #2D3748;
        }
      }
      &__control {
        .v-messages {
          display: none;
        }
      }
    }
  }
  .registered {
    max-width: 380px;
    margin: 0 auto;

    &__title {
      font-weight: 600;
      font-size: 28px;
      line-height: 136.02%;
      color: #27272E;
    }
    &__info-top-text {
      font-weight: 600;
      font-size: 20px;
      color: #2D3748;
    }
    &__info-text {
      font-weight: 400;
      font-size: 16px;
      color: #718096;
      span {
        font-weight: 600;
      }
    }
  }
  .gender-switcher {
    width: 198px;
    background-color: #F7FAFC;
    border-radius: 8px;
    padding: 2px;
    column-gap: 2px;

    .button {
      ::v-deep .v-btn {
        height: 32px !important;
        width: 96px;
        color: #425466;
        font-weight: 600;
        font-size: 12px;
        letter-spacing: 0;
        background-color: #F7FAFC;
        border-radius: 8px;
        padding: 0;
      }
    }
    .button.active-gender {
      ::v-deep .v-btn {
        background-color: #008C89;
        color: #FFFFFF;
      }
    }
  }
  .pwd-requirements-prompt {
    width: inherit;
    position: absolute;
    z-index: 4;
    right: 2px;
    top: 83px;
  }
  .button {
    ::v-deep .v-btn {
      font-weight: 600;
      font-size: 14px;
      letter-spacing: normal;
    }
  }

  .icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 12px;

    span {
      display: inline-block;
      width: 22px;
      height: 22px;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      margin-top: 4px;
      cursor: pointer;

      &.show-pass {
        background-image: url('@/assets/icons/show.svg');
      }

      &.hide-pass {
        background-image: url('@/assets/icons/hide.svg');
      }
    }
  }
}

::v-deep .v-text-field__details {
  padding-top: 5px !important;
}
@media screen and (max-width: 768px) {
  .container-wrapper {
    flex-direction: column;
    align-items: center;
  }
}
@media screen and (max-width: 350px) {
  .registration-from-qr {
    #authForm {
      .registration-from-qr__agree {
        display: unset !important;
        .checkbox {
          display: inline-block !important;
        }
      }
      .registration-from-qr__registration-link {
        display: unset !important;
        .link {
          margin-left: 0!important;
        }
      }
    }
  }
}
</style>
